// Load an almanac.v1 JSON export back into a report-ready pipelineState.
// Pure function — no I/O, no side effects. The inverse of
// buildAlmanacExport.js, and the "re-import flow" its header note promised.
//
// WHY THIS EXISTS: Yahoo revoked API access (Jul 2026, ecosystem-wide), so
// for Yahoo leagues the downloaded JSON is now the ONLY copy of their
// history — the fetch pipeline that originally built it returns 403 at
// every call. This module turns that archive back into the full report
// without touching any platform API. It is platform-agnostic on purpose:
// a Sleeper export loads the same way (offline, instant, no login).
//
// THE BIG WIN THAT MAKES THIS CHEAP: the export carries the computed
// `stats` object VERBATIM (buildAlmanacExport passes it straight through),
// and RecapReport renders almost entirely from `stats`. So we do NOT
// re-run computeFunStats against reconstructed matchups — that would buy
// nothing and risk drift if funStats logic has changed since the export
// was generated. The archive IS the output; we just re-hydrate the shell
// around it. Pre-pulled record-game lineups ride along inside stats too.
//
// What reconstructs losslessly:
//   - stats (verbatim), matchups (flattened back from seasons[].matchups —
//     serializeSeason filtered the flat array by year, so concat inverts it),
//   - championshipsByYear (inverted from standings[].finish: 1/2/3 were
//     written FROM champion/runnerUp/third, so reading them back is exact),
//   - aliasMap (from league.owners[].aliases — buildOwnerList grouped raw
//     names by canonical; alias → canonical re-creates the map).
//
// What degrades, deliberately and safely:
//   - pipelineState.seasons holds the export's SERIALIZED season slices
//     (keyed by `year`), not raw platform payloads (keyed by `season`) —
//     the export never carried raw payloads (they'd bloat it 10-20x).
//     computeLeagueEvolution filters on `s.season != null`, so the slices
//     drop out and the Format History section simply doesn't render.
//   - Lineup lazy-fetch for non-record games can't reach Yahoo anyway;
//     the pre-pulled record lineups inside stats still display.
//   - Re-DOWNLOADING from a loaded report round-trips league id + owner
//     aliases via the seasons[0] shims below, but scoringType/rosterSize
//     re-detect as custom/null (the detectors read raw settings we don't
//     have). The original file remains the authoritative archive.

const SUPPORTED_SCHEMA = "almanac.v1";

// Thrown for every validation failure so the caller can show ONE kind of
// error inline. `userMessage` is written for the landing page, not a log.
export class AlmanacImportError extends Error {
  constructor(userMessage) {
    super(userMessage);
    this.name = "AlmanacImportError";
    this.userMessage = userMessage;
  }
}

// jsonText → pipelineState (same shape RecapImport's onPipelineReady emits,
// so AuctionDraftBuilder can hand it to EmailGate/RecapReport unchanged).
export function loadAlmanacExport(jsonText) {
  let json;
  try {
    json = JSON.parse(jsonText);
  } catch {
    throw new AlmanacImportError(
      "That file isn't valid JSON. Make sure you picked the almanac file downloaded from this site (it ends in .json)."
    );
  }
  return pipelineStateFromExport(json);
}

export function pipelineStateFromExport(json) {
  if (!json || typeof json !== "object" || Array.isArray(json)) {
    throw new AlmanacImportError("That file doesn't look like a League Almanac export.");
  }
  if (json.schema !== SUPPORTED_SCHEMA) {
    // Distinguish "not ours" from "ours, but newer" — a future almanac.v2
    // should say so instead of pretending the file is corrupt.
    throw new AlmanacImportError(
      typeof json.schema === "string" && json.schema.startsWith("almanac.")
        ? `This export uses schema "${json.schema}" but this build reads ${SUPPORTED_SCHEMA}. Try re-downloading the JSON, or reload the page for a newer build.`
        : "That file doesn't look like a League Almanac export — no almanac.v1 schema marker found."
    );
  }
  const stats = json.stats;
  if (!stats || typeof stats !== "object" || !stats.meta) {
    throw new AlmanacImportError("This export is missing its stats payload, so there's no report to rebuild.");
  }
  const seasons = Array.isArray(json.seasons) ? json.seasons : [];
  if (!seasons.length) {
    throw new AlmanacImportError("This export has no seasons in it, so there's no report to rebuild.");
  }
  const league = json.league || {};
  const platform = league.platform || "yahoo";

  // Flat matchups array — serializeSeason filtered pipelineState.matchups
  // by year into each season slice; concatenating inverts that exactly.
  const matchups = seasons.flatMap((s) => (Array.isArray(s?.matchups) ? s.matchups : []));

  // championshipsByYear — deriveStandings wrote finish 1/2/3 FROM
  // {champion, runnerUp, third}; invert per season. Years where nobody
  // medaled (or standings are empty) get no entry, which is the same as
  // the adapters emit for unfinished seasons.
  const championshipsByYear = {};
  for (const s of seasons) {
    const rows = Array.isArray(s?.standings) ? s.standings : [];
    const entry = {};
    for (const r of rows) {
      if (r?.finish === 1) entry.champion = r.owner;
      else if (r?.finish === 2) entry.runnerUp = r.owner;
      else if (r?.finish === 3) entry.third = r.owner;
    }
    if (entry.champion || entry.runnerUp || entry.third) {
      championshipsByYear[String(s.year)] = entry;
    }
  }

  // aliasMap — league.owners[].aliases were grouped BY this map at export
  // time (buildOwnerList), so alias → canonical rebuilds it. Canonical
  // names need no entry: consumers do `aliasMap[raw] || raw`.
  const aliasMap = {};
  for (const o of Array.isArray(league.owners) ? league.owners : []) {
    if (!o?.canonical) continue;
    for (const a of Array.isArray(o.aliases) ? o.aliases : []) {
      if (a && a !== o.canonical) aliasMap[a] = o.canonical;
    }
  }

  // Round-trip shims, attached to a COPY of the first season slice only.
  // buildAlmanacExport reads seasons[0].league_key (yahoo) / .league_id
  // (sleeper) for the league id, and buildOwnerList walks seasons[].teams
  // (yahoo, nickname||name) / seasons[].users (sleeper, display_name) for
  // the owner list. Feeding it every known raw name on one season slice
  // makes a re-download of a LOADED report keep its league id and full
  // alias table. serializeSeason copies only its own fields, so none of
  // these shims leak into that re-export's seasons array.
  const rawNames = (Array.isArray(league.owners) ? league.owners : []).flatMap((o) =>
    [o?.canonical, ...(Array.isArray(o?.aliases) ? o.aliases : [])].filter(Boolean)
  );
  const first = { ...(seasons[0] || {}) };
  if (platform === "sleeper") {
    if (league.id != null) first.league_id = league.id;
    first.users = rawNames.map((n) => ({ display_name: n }));
  } else {
    if (league.id != null) first.league_key = league.id;
    first.teams = rawNames.map((n) => ({ nickname: n }));
  }
  const seasonsOut = [first, ...seasons.slice(1)];

  const yearsCovered = Array.isArray(stats.meta.yearsCovered) && stats.meta.yearsCovered.length
    ? stats.meta.yearsCovered
    : seasons.map((s) => String(s.year));

  return {
    platform,
    leagueMetadata: {
      name: league.name || "Your League",
      yearsCovered,
      ownerCount: stats.meta.totalOwners ?? null,
    },
    seasons: seasonsOut,
    matchups,
    championshipsByYear,
    stats,
    aliasMap,
    // Provenance marker — not read by the report today, but lets any
    // downstream consumer (analytics, debug) distinguish a loaded archive
    // from a live fetch without sniffing season shapes.
    loadedFromExport: {
      generatedAt: json.generatedAt || null,
      generatorVersion: json.generator?.version || null,
    },
  };
}
